import React, { useCallback, useEffect, useState } from 'react';
import { Users, CheckCircle2, Clock, XCircle, Loader2, RefreshCw } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';

const STATUS_META = {
  valid:         { label: 'Valid',        cls: 'bg-emerald-100 text-emerald-800' },
  expiring_30d:  { label: 'Expiring 30d', cls: 'bg-amber-100 text-amber-800' },
  expiring_7d:   { label: 'Expiring 7d',  cls: 'bg-orange-100 text-orange-800' },
  expired:       { label: 'Expired',      cls: 'bg-red-100 text-red-800' },
  missing:       { label: 'Missing',      cls: 'bg-slate-100 text-slate-600' },
  overridden:    { label: 'Overridden',   cls: 'bg-violet-100 text-violet-800' },
};

/**
 * Agency Compliance Panel — redacted view only. No SSN, DOB, address,
 * bank data, or raw file URLs. Fed by /api/hris/agency/compliance-expirations
 * which walks every linked worker and returns only rows with at least
 * one expiring / expired doc.
 */
export default function AgencyCompliancePanel() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiCall('/api/hris/agency/compliance-expirations');
      setWorkers(r.workers || []);
    } catch (e) {
      toast.error(e?.message || 'Could not load compliance');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="agency-compliance-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" /> Worker Compliance
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Certifications with upcoming expiries. Redacted view — file downloads live with the worker/admin.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
          data-testid="agency-compliance-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading && workers.length === 0 ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : workers.length === 0 ? (
        <p className="p-8 text-center text-sm text-slate-500 bg-white border border-slate-200 rounded-xl" data-testid="agency-compliance-empty">
          Nothing expiring in the near term. Nice work.
        </p>
      ) : (
        <ul className="space-y-3">
          {workers.map((w) => (
            <li key={w.worker.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`agency-compliance-worker-${w.worker.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-semibold text-slate-900">{w.worker.legal_name || w.worker.display_name}</p>
                  <p className="text-xs text-slate-500">
                    Work auth: <span className="font-semibold">{w.worker.work_authorization}</span>
                    <> · </>
                    <span className="text-red-700">{w.counts.expired} expired</span>
                    <> · </>
                    <span className="text-amber-700">{w.counts.expiring_soon} expiring soon</span>
                  </p>
                </div>
              </div>
              <ul className="mt-2 divide-y divide-slate-100">
                {w.documents
                  .filter((d) => d.status !== 'valid')
                  .map((d, i) => {
                    const meta = STATUS_META[d.status] || STATUS_META.missing;
                    return (
                      <li key={i} className="py-2 flex items-center justify-between text-sm">
                        <div>
                          <span className="font-semibold">{d.subtype}</span>
                          <span className="text-slate-500 text-xs">
                            {' — '}{d.display_name || d.category}
                            {d.expires_at && <> · {new Date(d.expires_at).toLocaleDateString()}</>}
                          </span>
                        </div>
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>
                          {meta.label}
                        </span>
                      </li>
                    );
                  })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
